// @flow

import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import PinImage from 'app/common/react/components/PinRep/components/PinVisual/PinImage';
import PinVideoEndCard from 'app/common/react/components/PinRep/components/PinVisual/PinVideoEndCard';
import type { Dispatch } from 'redux';
import VideoWithContextLogging from 'app/common/react/components/video/VideoWithContextLogging';
import { Box } from 'gestalt';
import { isPromotedPin } from 'app/common/lib/util/PinUtils';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { setCurrentVideo, type SetVideoParams } from 'app/common/redux/actions/app';

export type OwnProps = {|
  currentTime?: ?number,
  highResOnly?: boolean,
  inAdsDesktopVideoExperiment?: boolean,
  isAutoplay?: boolean,
  isExperimentalPinRep: boolean,
  lowResOnly?: boolean,
  onError?: () => void,
  onLoad?: () => void,
  onTimeChange?: ({ event: SyntheticEvent<HTMLVideoElement>, time: number }) => void,
  pin: Pin,
  playing: boolean,
  rootMargin?: string,
  saved?: boolean,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

type DispatchProps = {|
  setCurrentVideo: (SetVideoParams) => void,
|};

type Props = {| ...OwnProps, ...DispatchProps |};

export function PinVideo({
  inAdsDesktopVideoExperiment,
  isAutoplay,
  currentTime,
  lowResOnly,
  highResOnly,
  onLoad,
  onError,
  saved,
  onTimeChange,
  isExperimentalPinRep,
  pin,
  playing,
  rootMargin,
  setCurrentVideo,
  viewType,
  viewParameter,
}: Props) {
  const [showEndCard, setShowEndCard] = useState(false);
  const [showImagePlaceholder, setShowImagePlaceholder] = useState(true);
  // Promoted video pins in grid have different behavior
  const isPromotedVideo = isPromotedPin(pin);
  const isTwoTapPromotedVideo = isPromotedVideo && !pin.is_eligible_for_web_closeup;
  const isEndCardEligible =
    isExperimentalPinRep && isPromotedVideo && pin.is_eligible_for_web_closeup;
  // Organic (not promoted pins) and two-tap promoted video pins should loop.
  const shouldLoop = !isPromotedVideo || isTwoTapPromotedVideo || !isExperimentalPinRep;
  // If a video is not playing (PAUSED or LOADING) show PinImage
  let renderPinImage = !playing;
  // Ads desktop video experiment uses updated PinImage logic
  if (inAdsDesktopVideoExperiment) {
    // We use PinImage as a placeholder for performance reasons. This is nice for a case like Related Pins
    // where there is an entire grid full of videos. We do not want to render all those video components at once. Instead we only
    // render video components that are autoplaying and the rest as PinImages. However, once a video has started autoplaying
    // we never replace it back with PinImage because we want context logging to work after
    // the video has been paused.
    renderPinImage = showImagePlaceholder;
    // If it is safe to autoplay, the video is chosen to play, and we are currently showing PinImage then turn PinImage off
    if (isAutoplay && playing && showImagePlaceholder) {
      setShowImagePlaceholder(false);
    }
  }

  const { url: poster } = pin.images['236x'] || {};
  const { id: pinId, is_promoted: isPromoted } = pin;

  const onRestartVideo = useCallback(({ event }) => {
    event.preventDefault();
    event.stopPropagation();
    // reset time to 0
    setCurrentVideo({
      pinId,
      isPromoted,
      currentTime: 0,
    });
    setShowEndCard(false);
  }, []);

  const onVideoEnded = useCallback(
    () => {
      setShowEndCard(isEndCardEligible);
    },
    [isEndCardEligible],
  );

  // These should always be set but Flow wants to be certain
  if (!pin.videos || !pin.videos.video_list.V_HLSV4) {
    return null;
  }
  const { height, thumbnail, width, url } = pin.videos.video_list.V_HLSV4 || {};
  const videoId = pin.videos.id;
  const src = [{ type: 'video/m3u8', src: url }];

  // If the video has reached the end and is eligible, show the end card.
  // Whether Redux considers the video paused or not we want the endcard to take precedence over the image placeholder.
  // Note: a video that has end carded is still considered playing in Redux because instead of auto-looping we show end card
  if (showEndCard) {
    return (
      // Be careful removing this box, the endframe will not render
      <Box>
        <PinVideoEndCard
          backgroundImage={poster}
          naturalHeight={height}
          naturalWidth={width}
          pin={pin}
          onRestartVideo={onRestartVideo}
        />
      </Box>
    );
  }

  if (renderPinImage) {
    return (
      // Be careful removing this box, it seems to break autoplay
      <Box>
        <PinImage
          lowResOnly={lowResOnly}
          highResOnly={highResOnly}
          onLoad={onLoad}
          onError={onError}
          /* $FlowFixMe - Pin types don't match between PinRep and PinImage. */
          pin={pin}
          saved={saved}
        />
      </Box>
    );
  }

  // Otherwise return the video component
  return (
    // Be careful removing this box, it seems to break autoplay
    <Box>
      <VideoWithContextLogging
        rootMargin={rootMargin}
        inAdsDesktopVideoExperiment={inAdsDesktopVideoExperiment}
        contextLogData={{
          component: PCONST.thrift.ComponentType.FLOWED_PIN,
          view: viewType,
          viewParameter,
          objectId: pin.id,
          video_id: videoId,
        }}
        accessibilityMaximizeLabel={i18n._('Maximize', 'Maximize button on video')}
        accessibilityMinimizeLabel={i18n._('Minimize', 'Minimize button on video')}
        accessibilityMuteLabel={i18n._('Mute', 'Mute button on video')}
        accessibilityPauseLabel={i18n._('Pause', 'Pause button on video')}
        accessibilityPlayLabel={i18n._('Play', 'Play button on video')}
        accessibilityUnmuteLabel={i18n._('Unmute', 'Unmute button on video')}
        aspectRatio={width / height}
        captions=""
        hlsConfig={{
          capLevelToPlayerSize: true,
          startPosition: currentTime || -1,
        }}
        loop={shouldLoop}
        onTimeChange={onTimeChange}
        playing={playing}
        // even though poster is probably the same as image, keeping poster because it reduces
        // amount of time black screen shows before video loaded and starts playing
        poster={poster || thumbnail}
        src={src}
        // Pins should continue playing in grid where they left off when coming from closeup
        videoStartTime={currentTime}
        volume={0}
        onEnded={onVideoEnded}
      />
    </Box>
  );
}

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: OwnProps): DispatchProps => ({
  setCurrentVideo: (setVideoParams: SetVideoParams) => dispatch(setCurrentVideo(setVideoParams)),
});

export default connect<Props, OwnProps, _, DispatchProps, _, _>(
  undefined,
  mapDispatchToProps,
)(PinVideo);
